import $ from "jquery";
import {gsap} from "gsap/all";
import {Action} from "./Action";
import {CircleTimer} from "../CircleTimer";
import {cloneSvg} from "../SvgBank";

// Memorize a button sequence, then execute it. The buttons are identified by
// their console action name, the same the phidget map uses, each drawn from
// its design SVG (sequence-button-<action>). Phase one shows each button of
// the sequence (art plus text) one at a time. Phase two shows empty slots and
// the player replays the sequence: the right button fills the current slot
// with its art, a wrong one with the error mark, and the run has a global time
// limit. One point per slot.
export class MemoryAction extends Action {

  constructor(game, config) {
    super(game, config);

    // An entry of the sequence is the console action of its button, or an
    // object naming that button plus the gauge state the press moves to.
    // Both spellings are normalized to the object form here.
    this.sequence = (config.sequence || []).map((entry) => {
      return typeof entry === "string" ? {button: entry} : entry;
    });
    this.maxPoints = this.sequence.length;

    // How long each button of the sequence stays on screen while memorizing.
    this.showDurationSec = config.show_duration !== undefined ? config.show_duration : 2;
    // Time allowed to replay the whole sequence.
    this.executeDurationSec = config.execute_duration !== undefined ? config.execute_duration : 10;

    // Quick fade clearing a memorized button right before the next one; the
    // reveals reuse the game's swap fade, rise and stagger values.
    this.showOutSec = 0.2;

    // Phases run memorize then execute.
    this.phase = null;
    this.showIndex = -1;
    this.slotIndex = 0;

    this.$show = null;
    this.$slots = null;
    this.timer = null;
    this.showTimeline = null;
    this.slotsTween = null;
    this.holdCall = null;
  }

  start() {
    this.startMemorize();
  }

  build() {
    this.$show = $(`
      <div class="memory-show">
        <div class="button"></div>
        <div class="text">
          <div class="i18n fading-i18n">
            <div class="fr"></div>
            <div class="en"></div>
          </div>
        </div>
      </div>
    `);

    // One dashed box from the design per sequence entry, the shared arrows
    // between them. The fill layer receives the button art or the error mark
    // when the slot resolves.
    const $slots = $('<div class="memory-slots"></div>');
    this.sequence.forEach((action, i) => {
      if (i > 0) $slots.append($('<div class="press-arrow"></div>').append(cloneSvg("spacer-arrow")));

      const $slot = $('<div class="memory-slot"><div class="base"></div><div class="fill"></div></div>');
      $slot.find(".base").append(cloneSvg("sequence-slot"));
      $slots.append($slot);
    });

    this.$host.append(this.$show, $slots.hide());
    this.$slots = $slots;

    // Created here so the timer face fades in with the rest of the content.
    this.timer = new CircleTimer(this.game.$actionContainer.find(".corner-timer"));
  }

  startMemorize() {
    this.phase = "memorize";

    // The memorize label is already up: launchAction sets it for the whole
    // action, setting it again here would replay the animation.

    // One timer across the whole memorize phase.
    this.timer.start(this.sequence.length * this.showDurationSec);
    this.showNext();
  }

  showNext() {
    this.showIndex++;

    if (this.showIndex >= this.sequence.length) {
      this.startExecute();
      return;
    }

    const action = this.sequence[this.showIndex].button;
    const button = this.buttonById(action);

    this.$show.find(".button").empty().append(cloneSvg(`sequence-button-${action}`));

    // Dev help while the settings ask for it: prefix the text with the console
    // key standing in for the button.
    const text = (button && button.text) || {fr: "", en: ""};
    const devKey = this.app.getShowConsoleKeys()
      ? `${this.app.keypressForAction(action)} - `
      : "";
    this.game.fillI18n(this.$show.find(".text"), {
      fr: `${devKey}${text.fr || ""}`,
      en: `${devKey}${text.en || ""}`
    });

    // Each button rises in, holds, then quickly fades right before its
    // display time is up, so the next one (or the slot row) enters clean.
    this.showTimeline = gsap.timeline({onComplete: () => this.showNext()});
    this.showTimeline.fromTo(this.$show, {
      autoAlpha: 0,
      y: this.game.swapRiseY
    }, {
      autoAlpha: 1,
      y: 0,
      duration: this.game.swapFadeSec,
      ease: "power2.out"
    });
    this.showTimeline.to(this.$show, {
      autoAlpha: 0,
      duration: this.showOutSec,
      ease: "power1.in"
    }, this.showDurationSec - this.showOutSec);
  }

  startExecute() {
    this.phase = "execute";
    this.slotIndex = 0;

    this.game.setActionLabel(this.config.label_execute);
    this.$show.hide();
    this.$slots.show();

    // The empty boxes and their arrows rise in one after the other.
    this.slotsTween = gsap.fromTo(this.$slots.children(), {
      autoAlpha: 0,
      y: this.game.swapRiseY
    }, {
      autoAlpha: 1,
      y: 0,
      duration: this.game.swapFadeSec,
      ease: "power2.out",
      stagger: this.game.swapStaggerSec
    });

    this.timer.start(this.executeDurationSec, () => this.onTimeUp());
  }

  onButtonPress(action) {
    if (this.phase !== "execute" || this.slotIndex >= this.sequence.length) return;

    const entry = this.sequence[this.slotIndex];
    const success = action === entry.button;
    this.fillSlot(this.slotIndex, success);
    if (success) this.points++;
    this.playInputSound(success);

    // An entry naming a gauge state moves the gauge as soon as its slot is
    // resolved, right or wrong, instead of waiting for the end of the action.
    this.setGaugeState(entry.gauge);

    this.slotIndex++;
    if (this.slotIndex >= this.sequence.length) this.finishExecute();
  }

  // A resolved slot fills with the sequence success mark on a right press,
  // the error mark on a wrong one or when the time runs out.
  fillSlot(index, success) {
    const $slot = this.$slots.find(".memory-slot").eq(index);
    $slot.addClass(success ? "success" : "fail");
    $slot.find(".fill").append(cloneSvg(success ? "sequence-button-success" : "sequence-button-error"));
  }

  onTimeUp() {
    if (this.phase !== "execute") return;

    // Whatever was not entered in time counts as failed. The gauge skips
    // straight to the state of the last of them, so it does not stay behind
    // where the player stopped pressing.
    // One wrong sound for all the slots left, not one per slot.
    if (this.slotIndex < this.sequence.length) this.playInputSound(false);

    let lastGauge = null;
    for (let i = this.slotIndex; i < this.sequence.length; i++) {
      this.fillSlot(i, false);
      if (this.sequence[i].gauge) lastGauge = this.sequence[i].gauge;
    }
    this.slotIndex = this.sequence.length;

    this.setGaugeState(lastGauge);

    this.finishExecute();
  }

  finishExecute() {
    this.phase = "done";
    this.timer.stop();

    // The verdict only shows once the run is over, during the hold: a perfect
    // sequence gets the success message, anything else the error one.
    this.showVerdict(this.points === this.maxPoints);

    // Let the state read for a beat before moving on.
    this.holdCall = gsap.delayedCall(this.game.stateHoldSec, () => this.complete());
  }

  destroy() {
    if (this.showTimeline) this.showTimeline.kill();
    if (this.slotsTween) this.slotsTween.kill();
    if (this.holdCall) this.holdCall.kill();
    if (this.timer) {
      this.timer.destroy();
      this.timer = null;
    }
  }
}
